import { useEffect, useId, useRef, useState } from 'react'
import { ZoomIn } from 'lucide-react'
import { BANNER_FILL_CARD, BANNER_FILL_HEADER, MIN_BANNER_SCALE, MAX_BANNER_SCALE, bannerImageSize, bannerOverlay, bannerPositionOf, bannerScaleOf, cardScrim, defaultBannerPosition } from '../../../../../shared/pkg/prepacks/templateBuilder.js'
import { FALLBACK_CARD_ASPECT, FALLBACK_HEADER_ASPECT } from './useCardAspect'

const KEY_STEPS = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }
// One axis of a drag: how far the picture can move in pixels, and which way a pointer move takes the focal point.
const withAxis = (axis, { room, sign }) => ({ [`room${axis}`]: room, [`sign${axis}`]: sign })

/**
 * Framing: a box of the picture's shape on the card showing the picture as
 * the card will crop it. Dragging (or the arrow keys, 5% a step, 1% with
 * Shift) moves the picture, which is the focal point in `position`; the
 * slider zooms (`scale`). The percentages are CSS background-position: a
 * picture as wide as the box cannot move sideways, which is also what the
 * slider's left end means.
 *
 * `fill` says where the picture goes. Behind the header the box has the
 * header's shape (`headerAspect`, width over height, measured from the
 * preview: 5:1 for a row header or a band, taller for a hero header with the
 * title over the picture), is washed with `main` (the header color) and the
 * zoom is times the banner's width. Behind the whole card (`card`) the box has
 * the card's shape (`aspect`, measured the same way), wears the card's scrim
 * in `main` (the banner fade color) and the zoom is times the card's height.
 *
 * `wash` is the layer the card actually draws over the picture when it is not
 * the fill's usual one (#946): `null` for none (a band above the header has no
 * title on it), or a CSS image such as the even wash under a centered title.
 * Left out, the box draws the header wash or the card scrim as before.
 */
export default function BannerFraming({ picture, main, fill = BANNER_FILL_HEADER, aspect = FALLBACK_CARD_ASPECT, headerAspect = FALLBACK_HEADER_ASPECT, position, scale, disabled, wash, onChange }) {
  const id = useId()
  const drag = useRef(null)
  const card = fill === BANNER_FILL_CARD
  const overlay = wash === undefined ? (card ? cardScrim(main) : bannerOverlay(main)) : wash
  // What the probe learned about `picture`: its aspect, or that it does not load. Keyed by
  // the URL so a swapped picture never drags with the old one's aspect.
  const [probe, setProbe] = useState(null)
  const { aspect: pictureAspect, broken } = probe?.src === picture ? probe : { aspect: 16 / 9, broken: false }
  const [defaultX, defaultY] = bannerPositionOf(defaultBannerPosition(fill))
  const [x, y] = bannerPositionOf(position) || [defaultX, defaultY]
  const zoom = bannerScaleOf(scale)
  useEffect(() => {
    if (!picture) return undefined
    const image = new Image()
    image.onload = () => setProbe({ src: picture, aspect: image.naturalHeight ? image.naturalWidth / image.naturalHeight : 16 / 9, broken: false })
    image.onerror = () => setProbe({ src: picture, aspect: 16 / 9, broken: true })
    image.src = picture
    return () => { image.onload = null; image.onerror = null }
  }, [picture])
  // A drag in progress ends when the control is disabled under it (a paint starting, for one).
  useEffect(() => { if (disabled) drag.current = null }, [disabled])
  const live = Boolean(picture) && !broken && !disabled
  const setFraming = (nextX, nextY, nextScale) => {
    const rx = Math.min(100, Math.max(0, Math.round(nextX)))
    const ry = Math.min(100, Math.max(0, Math.round(nextY)))
    const rs = Math.round(nextScale * 100) / 100
    // Pointer moves mostly round to the same percent; only a real change re-renders the card.
    if (rx === x && ry === y && rs === zoom) return
    onChange({ position: `${rx}% ${ry}%`, scale: rs })
  }
  const onPointerDown = (event) => {
    if (!live || !event.isPrimary || event.button !== 0) return
    event.currentTarget.setPointerCapture(event.pointerId)
    // Behind the header the picture is `zoom` box-widths wide and its height follows its own
    // aspect; behind the card it is `zoom` box-heights tall and its width follows. Moving it by
    // one pixel shifts the focal point by that share of the room it has to move.
    // A zoomed or tall picture can have far more room than the box: a drag of about one box
    // width (or three banner heights, one card height) always spans the whole range, so the edge is one drag away.
    // A picture smaller than the box on an axis (a square picture on a short, wide card) moves the
    // other way: the percentage then places it along the uncovered strip, so a larger value shows
    // it further right or down, where a larger picture's shows more of its right or bottom.
    const box = event.currentTarget.getBoundingClientRect()
    const imageW = card ? box.height * zoom * pictureAspect : box.width * zoom
    const imageH = card ? box.height * zoom : imageW / pictureAspect
    const room = (size, boxSize, cap) => ({ room: Math.min(Math.abs(size - boxSize), cap), sign: size >= boxSize ? -1 : 1 })
    drag.current = { startX: event.clientX, startY: event.clientY, x, y, ...withAxis('X', room(imageW, box.width, box.width)), ...withAxis('Y', room(imageH, box.height, card ? box.height : box.height * 3)) }
  }
  const onPointerMove = (event) => {
    const d = drag.current
    if (!d || disabled) return
    const nextX = d.roomX > 0 ? d.x + d.signX * ((event.clientX - d.startX) / d.roomX) * 100 : x
    const nextY = d.roomY > 0 ? d.y + d.signY * ((event.clientY - d.startY) / d.roomY) * 100 : y
    setFraming(nextX, nextY, zoom)
  }
  const onPointerUp = () => { drag.current = null }
  const onKeyDown = (event) => {
    if (!live) return
    const step = KEY_STEPS[event.key]
    if (step) {
      event.preventDefault()
      const by = event.shiftKey ? 1 : 5
      setFraming(x + step[0] * by, y + step[1] * by, zoom)
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault()
      setFraming(event.key === 'Home' ? 0 : 100, y, zoom)
    }
  }
  const isDefault = x === defaultX && y === defaultY && zoom === MIN_BANNER_SCALE
  // The zoom is times the banner's width, or for the card times the card's height.
  const times = card ? 'times the card’s height' : 'times'
  const hint = card ? 'Drag or arrow keys to move, slide to zoom (times the card’s height).' : 'Drag or arrow keys to move, slide to zoom.'
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-xs">
        <span className="font-medium">Framing</span>
        <span id={`${id}-hint`} className="min-w-0 flex-1 truncate text-[var(--prepacks-muted)]" title={card ? 'Drag the picture or use the arrow keys to move it; slide to zoom, in card heights.' : 'Drag the picture or use the arrow keys to move it; slide to zoom.'}>{hint}</span>
        {/* Always mounted so the row never changes height the moment a drag starts. */}
        <button type="button" disabled={disabled || isDefault} aria-hidden={isDefault || undefined} tabIndex={isDefault ? -1 : 0} onClick={() => setFraming(defaultX, defaultY, MIN_BANNER_SCALE)}
          className={`-my-1 shrink-0 px-1 py-1 text-[var(--prepacks-accent-text)] hover:underline disabled:opacity-50 ${isDefault ? 'invisible' : ''}`}>Reset</button>
      </div>
      <div role="group" aria-roledescription="focal point" aria-label="Banner picture framing" aria-describedby={`${id}-hint`} aria-disabled={disabled || undefined}
        tabIndex={live ? 0 : -1} onKeyDown={onKeyDown} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
        className={`relative w-full ${card ? 'max-w-[240px]' : ''} touch-none select-none overflow-hidden rounded-lg border border-[var(--prepacks-border)] ${live ? 'cursor-grab active:cursor-grabbing' : ''} ${disabled ? 'opacity-60' : ''}`}
        style={{
          aspectRatio: card ? aspect : headerAspect,
          background: picture && !broken
            ? `${overlay ? `${overlay}, ` : ''}url("${picture}") ${x}% ${y}% / ${bannerImageSize(zoom, fill)} no-repeat, ${main}`
            : 'var(--prepacks-raised)',
        }}>
        <span className="sr-only" aria-live="polite">Picture at {x}% across, {y}% down, {zoom.toFixed(2)} {card ? times : 'times zoom'}</span>
      </div>
      {!picture || broken ? <p role="status" className="text-[11px] text-[var(--prepacks-muted)]">{broken ? 'The picture can’t load here, so it can’t be framed.' : 'Pick a picture to frame it.'}</p> : null}
      <div className="flex items-center gap-2 text-xs text-[var(--prepacks-muted)]">
        <ZoomIn className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <input type="range" name="banner-zoom" aria-label="Zoom" aria-valuetext={`${zoom.toFixed(2)} ${times}`} min={MIN_BANNER_SCALE} max={MAX_BANNER_SCALE} step={0.05} value={zoom} disabled={!live}
          onChange={(event) => setFraming(x, y, Number(event.target.value))} className="w-full accent-[var(--prepacks-accent)]" />
        <span className="w-10 shrink-0 text-right tabular-nums">{zoom.toFixed(2)}×</span>
      </div>
    </div>
  )
}
